import { CheckCircleIcon, XCircleIcon, ClockIcon, AlertTriangleIcon } from 'lucide-react';
import { User } from '../types/user';

interface EmailStatusIndicatorProps {
  user: User;
  showResendButton?: boolean;
  onResendVerification?: () => void;
  onSendPasswordReset?: () => void;
  resending?: boolean;
}

export function EmailStatusIndicator({ 
  user, 
  showResendButton = false, 
  onResendVerification,
  onSendPasswordReset,
  resending = false 
}: EmailStatusIndicatorProps) {
  const getEmailStatus = () => {
    if (user.emailVerified) {
      return {
        status: 'verified',
        icon: CheckCircleIcon,
        text: 'Email Verified',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        description: user.emailVerifiedAt 
          ? `Verified on ${new Date(user.emailVerifiedAt).toLocaleDateString()}`
          : 'Email address has been verified'
      };
    }

    if (user.lastEmailSentAt) {
      const lastSent = new Date(user.lastEmailSentAt);
      const now = new Date();
      const hoursSinceLastSent = (now.getTime() - lastSent.getTime()) / (1000 * 60 * 60);
      
      if (hoursSinceLastSent < 24) {
        return {
          status: 'pending',
          icon: ClockIcon,
          text: 'Verification Pending',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          description: `Verification email sent ${Math.round(hoursSinceLastSent)} hours ago`
        };
      } else {
        return {
          status: 'expired',
          icon: AlertTriangleIcon,
          text: 'Verification Expired',
          color: 'text-orange-600',
          bgColor: 'bg-orange-50',
          borderColor: 'border-orange-200',
          description: 'Verification link has expired, resend required'
        };
      }
    }

    return {
      status: 'not_verified',
      icon: XCircleIcon,
      text: 'Email Not Verified',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      description: 'User has not verified their email address'
    };
  };

  const emailStatus = getEmailStatus();
  const Icon = emailStatus.icon;

  return (
    <div className={`p-4 rounded-lg border ${emailStatus.bgColor} ${emailStatus.borderColor}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-3">
          <Icon className={`h-5 w-5 ${emailStatus.color} flex-shrink-0`} />
          <div>
            <p className={`font-medium ${emailStatus.color}`}>
              {emailStatus.text}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {emailStatus.description}
            </p>
          </div>
        </div>
        
        {showResendButton && (
          <div className="ml-4 flex flex-col space-y-2">
            {!user.emailVerified && onResendVerification && (
              <button
                type="button"
                onClick={onResendVerification}
                disabled={resending}
                className="px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resending ? 'Sending...' : 'Resend Verification'}
              </button>
            )}
            
            {user.emailVerified && onSendPasswordReset && (
              <button
                type="button"
                onClick={onSendPasswordReset}
                disabled={resending}
                className="px-3 py-1 text-sm font-medium text-green-600 bg-green-50 border border-green-200 rounded-md hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resending ? 'Sending...' : 'Send Password Reset'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

interface EmailStatusBadgeProps {
  user: User;
  size?: 'sm' | 'md';
}

export function EmailStatusBadge({ user, size = 'sm' }: EmailStatusBadgeProps) {
  const getEmailStatus = () => {
    if (user.emailVerified) {
      return {
        text: 'Verified',
        color: 'bg-green-100 text-green-800',
        icon: CheckCircleIcon
      };
    }

    if (user.lastEmailSentAt) {
      const lastSent = new Date(user.lastEmailSentAt);
      const now = new Date();
      const hoursSinceLastSent = (now.getTime() - lastSent.getTime()) / (1000 * 60 * 60);
      
      if (hoursSinceLastSent < 24) {
        return {
          text: 'Pending',
          color: 'bg-yellow-100 text-yellow-800',
          icon: ClockIcon
        };
      } else {
        return {
          text: 'Expired',
          color: 'bg-orange-100 text-orange-800',
          icon: AlertTriangleIcon
        };
      }
    }

    return {
      text: 'Not Verified',
      color: 'bg-red-100 text-red-800',
      icon: XCircleIcon
    };
  };

  const emailStatus = getEmailStatus();
  const Icon = emailStatus.icon;
  const iconSize = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';
  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';
  const padding = size === 'sm' ? 'px-2 py-1' : 'px-3 py-1';

  return (
    <span className={`inline-flex items-center gap-1 ${padding} rounded-full font-medium ${textSize} ${emailStatus.color}`}>
      <Icon className={iconSize} />
      {emailStatus.text}
    </span>
  );
}
